import { create } from 'zustand';
import { mockGenerateSequenceFromChat } from './sequenceBuilderMockData';
import {
  recallCallTemplates,
  recallEmailTemplates,
  recallSmartTags,
} from './sequenceBuilderMockData';
import type { BlockType } from './sequenceBuilderTypes';
import type { SmartTag } from './sequenceBuilderMockData';
import { buildMockSequenceDocument } from './wizardMockData';
import { getRecallLocationId, getRecallRuntime } from './recallRuntime';

const MOCK_LOCATION_ID = 1;

/**
 * Recall Sequence Builder API.
 *
 * The function signatures for the pre-existing calls are unchanged so
 * `SequenceBuilderView` / `SequenceTestView` keep working. When a real backend
 * transport has been installed (the host app publishes an axios-backed
 * `get/post/patch/put/del` transport on `globalThis.__ELVA_RECALL_RUNTIME__` at
 * bootstrap — see `apps/recall/src/data/recallRuntime.ts`), each call hits the
 * real Recall endpoints documented in RECALL_SEQUENCE_API.md:
 *
 *   getTemplateJson    -> GET    /api/recall/v1/sequence-templates/{id}/json/   (§12)
 *   getSequenceDetail  -> GET    /api/recall/v1/sequences/{id}/json/            (§6)
 *   getSequenceFull    -> GET    /api/recall/v1/sequences/{id}/                 (§2)
 *   listSequences      -> GET    /api/recall/v1/sequences/                      (§1)
 *   createSequence     -> POST   /api/recall/v1/sequences/save/                 (§3)
 *   patchSequence      -> PATCH  /api/recall/v1/sequences/{id}/                 (§4)
 *   archiveSequence    -> DELETE /api/recall/v1/sequences/{id}/                 (§5)
 *   cloneFromSibling   -> POST   /api/recall/v1/sequences/clone-from-sibling/   (§7)
 *   generateScaffold   -> POST   /api/recall/v1/sequences/generate/            (§8)
 *   getPreview         -> GET    /api/recall/v1/sequences/{id}/preview/        (§9)
 *   testSend           -> POST   /api/recall/v1/sequences/{id}/test-send/      (§10)
 *   getVersionHistory  -> GET    /api/recall/v1/sequences/{id}/version-history/ (§11)
 *   listSequenceTemplates -> GET /api/recall/v1/sequence-templates/            (§12)
 *   getBlocksPalette   -> GET    /api/recall/v1/sequence-builder/blocks/       (§13)
 *   listMessageTemplates -> GET  /api/recall/message-templates/               (related)
 *   listSmartTags      -> GET    /api/recall/v1/smart-tags/                    (related)
 *
 * Trigger/action/condition enums already match the backend. The one contract
 * difference is action node `properties`: the editor tracks
 * `emailTemplateId` / `callTemplateId` / `delayAmount`, while the backend
 * `json_data` uses `emailTemplateSlug` / `callTemplateSlug` / `delayValue`
 * (plus `timerMode`). `mapJsonDataForWire` / `mapJsonDataFromWire` bridge the two
 * additively (they never drop the editor keys), so trees round-trip cleanly in
 * both directions without touching the in-canvas DSL.
 *
 * GAPs kept on mock fallback (no matching backend endpoint in the spec):
 *  - generateForStep: conversational per-block / whole-sequence AI editing.
 *    §8 `generate/` is a deterministic scaffold (see generateScaffold), not an
 *    AI editor, so the AI sidebars keep their mock behaviour.
 *  - startFromStarter: no dedicated "starter for this recall" endpoint; falls
 *    back to the sequence-templates JSON when a template slug is supplied.
 */

const RECALL_BASE = '/api/recall';
const RECALL_V1 = '/api/recall/v1';

export const useRecallLocationStore = create<{ selectedLocationId: number }>(() => ({
  // Seed from the host app's published location id when available (real backend),
  // otherwise fall back to the mock location for Storybook / standalone.
  selectedLocationId: getRecallLocationId() ?? MOCK_LOCATION_ID,
}));

function createSampleDoc(segmentName?: string, withTree = true) {
  return buildMockSequenceDocument(segmentName, withTree);
}

async function delay(ms: number) {
  await new Promise((r) => setTimeout(r, ms));
}

// ─── json_data property boundary adapters ───────────────────────────────────────
// The editor's node `properties` and the backend `json_data` disagree on a few
// key names. These walkers copy values across additively (source keys are never
// removed), so a tree saved by the editor validates on the backend and a tree
// authored on the backend renders in the editor.

type WireNode = {
  properties?: Record<string, unknown> | null;
  next?: WireNode | null;
  branches?: { yes?: WireNode | null; no?: WireNode | null } | null;
  triggerBranches?: WireNode[] | null;
  [key: string]: unknown;
};

type PropsMapper = (props: Record<string, unknown>) => Record<string, unknown>;

function setIfAbsent(target: Record<string, unknown>, key: string, value: unknown) {
  if (value !== undefined && value !== null && target[key] === undefined) {
    target[key] = value;
  }
}

/** Editor keys -> backend `json_data` keys (RECALL_SEQUENCE_API.md schema). */
function mapPropsForWire(props: Record<string, unknown>): Record<string, unknown> {
  const out = { ...props };
  setIfAbsent(out, 'emailTemplateSlug', props.emailTemplateId);
  setIfAbsent(out, 'callTemplateSlug', props.callTemplateId);
  setIfAbsent(out, 'delayValue', props.delayAmount);
  if (props.timerEnabled === true) setIfAbsent(out, 'timerMode', props.timerMode ?? 'wait');
  return out;
}

/** Backend `json_data` keys -> editor keys, so backend-authored trees render. */
function mapPropsFromWire(props: Record<string, unknown>): Record<string, unknown> {
  const out = { ...props };
  const genericSlug = props.message_template_slug ?? props.templateSlug;
  setIfAbsent(out, 'emailTemplateId', props.emailTemplateSlug ?? genericSlug);
  setIfAbsent(out, 'callTemplateId', props.callTemplateSlug);
  setIfAbsent(out, 'delayAmount', props.delayValue);
  return out;
}

function mapNodeTree(node: WireNode | null | undefined, mapper: PropsMapper): WireNode | null {
  if (!node || typeof node !== 'object') return (node ?? null) as WireNode | null;
  const out: WireNode = { ...node };
  if (out.properties && typeof out.properties === 'object') {
    out.properties = mapper(out.properties as Record<string, unknown>);
  }
  if (out.next) out.next = mapNodeTree(out.next, mapper);
  if (out.branches && typeof out.branches === 'object') {
    out.branches = {
      yes: mapNodeTree(out.branches.yes ?? null, mapper),
      no: mapNodeTree(out.branches.no ?? null, mapper),
    };
  }
  if (Array.isArray(out.triggerBranches)) {
    out.triggerBranches = out.triggerBranches.map((n) => mapNodeTree(n, mapper) as WireNode);
  }
  return out;
}

function mapJsonData(jsonData: unknown, mapper: PropsMapper): unknown {
  if (!jsonData || typeof jsonData !== 'object') return jsonData;
  const jd = jsonData as Record<string, unknown>;
  if (jd.rootNode === undefined) return jsonData;
  return { ...jd, rootNode: mapNodeTree(jd.rootNode as WireNode | null, mapper) };
}

const mapJsonDataForWire = (jsonData: unknown) => mapJsonData(jsonData, mapPropsForWire);
const mapJsonDataFromWire = (jsonData: unknown) => mapJsonData(jsonData, mapPropsFromWire);

/** Recall endpoints scope requests with `location_id` as a query param (not body). */
function locationQuery(locationId?: number): Record<string, unknown> | undefined {
  return locationId != null ? { location_id: locationId } : undefined;
}

/** Wire smart-tag row from GET /v1/smart-tags/ → editor insert shape. */
function adaptSmartTagFromWire(tag: Record<string, unknown>): SmartTag | null {
  const path = String(tag.path ?? '').trim();
  const key = String(tag.key ?? '').trim();
  const value = path || (key ? `{{${key}}}` : '');
  if (!value) return null;
  const label = String(tag.label ?? key.replace(/[._]/g, ' ') ?? value);
  const result: SmartTag = { value, label };
  if (tag.description) result.description = String(tag.description);
  if (tag.example_rendering) result.example = String(tag.example_rendering);
  if (tag.scope) result.scope = String(tag.scope);
  return result;
}

function parseSmartTagsResponse(data: unknown): SmartTag[] {
  const raw = Array.isArray(data)
    ? data
    : (data as { tags?: unknown[] })?.tags
      ?? (data as { results?: unknown[] })?.results
      ?? [];
  return raw
    .map((t) => adaptSmartTagFromWire(t as Record<string, unknown>))
    .filter((t): t is SmartTag => t != null);
}

export const sequenceBuilderApi = {
  async getTemplateJson(templateId: string, locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.get<{ id: number; json_data: unknown; title?: string }>(
        `${RECALL_V1}/sequence-templates/${templateId}/json/`,
        { location_id: locationId },
      );
      return { data: { ...data, json_data: mapJsonDataFromWire(data.json_data) } };
    }
    await delay(200);
    const doc = createSampleDoc('Starter template');
    return { data: { json_data: doc, id: Number(templateId) || 1 } };
  },

  async getSequenceDetail(sequenceId: number, locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.get<{
        id: number;
        name: string;
        description: string;
        tags: string[];
        json_data: unknown;
        status: string;
      }>(`${RECALL_V1}/sequences/${sequenceId}/json/`, { location_id: locationId });
      return { data: { ...data, json_data: mapJsonDataFromWire(data.json_data) } };
    }
    await delay(200);
    const names: Record<number, string> = {
      101: 'Standard hygiene sequence',
      102: 'Implant hygiene sequence',
      103: 'Perio maintenance sequence',
      901: 'Standard follow-up sequence',
      902: 'Implant post-op sequence',
      903: 'Perio maintenance sequence',
    };
    return {
      data: {
        id: sequenceId,
        name: names[sequenceId] ?? 'Recall sequence',
        description: 'Mock sequence for wizard demo',
        tags: ['mock', 'wizard'],
        json_data: createSampleDoc(names[sequenceId] ?? 'Recall sequence', false),
        status: 'DRAFT' as const,
      },
    };
  },

  /** §2 — full RecallSequenceSerializer (metadata + validation + counts). */
  async getSequenceFull(sequenceId: number, locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.get<Record<string, unknown>>(
        `${RECALL_V1}/sequences/${sequenceId}/`,
        { location_id: locationId },
      );
      return { data: { ...data, json_data: mapJsonDataFromWire(data.json_data) } };
    }
    await delay(200);
    return {
      data: {
        id: sequenceId,
        name: 'Recall sequence',
        status: 'DRAFT',
        json_data: createSampleDoc('Recall sequence', false),
        node_count: 0,
        action_count: 0,
        channels: [],
        validation_errors: [],
      },
    };
  },

  /** §1 — list active sequences for a recall / segment. */
  async listSequences(
    params: {
      location_recall_id?: number;
      location_segment_id?: number;
      location_id?: number;
      search?: string;
    } = {},
    locationId?: number,
  ) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.get<unknown[]>(`${RECALL_V1}/sequences/`, {
        ...params,
        location_id: params.location_id ?? locationId,
      });
      return { data };
    }
    await delay(200);
    return { data: [] as unknown[] };
  },

  async createSequence(payload: Record<string, unknown>, locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      const { location_id: _bodyLoc, ...body } = payload;
      const loc = (payload.location_id as number | undefined) ?? locationId;
      const data = await rt.post<{ id: number }>(
        `${RECALL_V1}/sequences/save/`,
        { ...body, json_data: mapJsonDataForWire(payload.json_data) },
        locationQuery(loc),
      );
      return { data };
    }
    await delay(300);
    return { data: { id: payload.location_segment_id ? 101 : 102, ...payload } };
  },

  async patchSequence(id: string | number, payload: Record<string, unknown>, locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      const { location_id: _bodyLoc, ...body } = payload;
      const loc = (payload.location_id as number | undefined) ?? locationId;
      const data = await rt.patch<{ id: number }>(
        `${RECALL_V1}/sequences/${id}/`,
        { ...body, json_data: mapJsonDataForWire(payload.json_data) },
        locationQuery(loc),
      );
      return { data };
    }
    await delay(300);
    return { data: { id: Number(id), ...payload } };
  },

  /** §5 — soft-delete (status=ARCHIVED). */
  async archiveSequence(id: string | number, locationId?: number) {
    const rt = getRecallRuntime();
    if (rt?.del) {
      const data = await rt.del<unknown>(`${RECALL_V1}/sequences/${id}/`, {
        location_id: locationId,
      });
      return { data };
    }
    await delay(200);
    return { data: { id: Number(id), status: 'ARCHIVED' } };
  },

  async startFromStarter(payload: Record<string, unknown>, locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      // §12: load starter tree by template id or slug.
      const templateKey =
        (payload.starter_template_id as string | number | undefined) ??
        (payload.template_type_slug as string | undefined);
      if (templateKey != null && templateKey !== '') {
        const data = await rt.get<{ json_data?: unknown }>(
          `${RECALL_V1}/sequence-templates/${templateKey}/json/`,
          locationQuery(locationId),
        );
        return { data: { ...data, json_data: mapJsonDataFromWire(data.json_data) } };
      }
    }
    await delay(300);
    return { data: { json_data: createSampleDoc('Starter template') } };
  },

  async cloneFromSibling(payload: Record<string, unknown>, locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      const { location_id: _bodyLoc, ...body } = payload;
      const loc = (payload.location_id as number | undefined) ?? locationId;
      const data = await rt.post<{ json_data?: unknown }>(
        `${RECALL_V1}/sequences/clone-from-sibling/`,
        body,
        locationQuery(loc),
      );
      return { data: { ...data, json_data: mapJsonDataFromWire(data.json_data) } };
    }
    await delay(300);
    return { data: { json_data: createSampleDoc('Cloned sequence') } };
  },

  /**
   * §8 — deterministic starter scaffold from a goal type (no LLM). Body is
   * `{ goal_type_slug, location_recall_id, template_type_slug? }`.
   */
  async generateScaffold(
    payload: {
      goal_type_slug: string;
      location_recall_id?: number;
      template_type_slug?: string;
      location_id?: number;
    },
    locationId?: number,
  ) {
    const rt = getRecallRuntime();
    if (rt) {
      const { location_id: _loc, ...body } = payload;
      const data = await rt.post<{ json_data?: unknown; validation_errors?: string[] }>(
        `${RECALL_V1}/sequences/generate/`,
        body,
        locationQuery(locationId),
      );
      return { data: { ...data, json_data: mapJsonDataFromWire(data.json_data) } };
    }
    await delay(400);
    const tree = await mockGenerateSequenceFromChat('trigger-scheduled' as BlockType, 'scaffold');
    return { data: { json_data: { rootNode: tree }, validation_errors: [] } };
  },

  /**
   * GAP: conversational block / whole-sequence AI editing. There is no matching
   * backend endpoint in RECALL_SEQUENCE_API.md; the deterministic §8 scaffolder
   * lives in `generateScaffold`. Always uses mock — never calls the backend.
   */
  async generateForStep(_payload: Record<string, unknown>, _locationId?: number) {
    await delay(500);
    const tree = await mockGenerateSequenceFromChat('trigger-scheduled' as BlockType, 'ai');
    return { data: { json_data: { rootNode: tree } } };
  },

  /** §9 — render each action step with template substitution (no send). */
  async getPreview(sequenceId: number, samplePatientId?: number, locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.get<Record<string, unknown>>(
        `${RECALL_V1}/sequences/${sequenceId}/preview/`,
        { sample_patient_id: samplePatientId, location_id: locationId },
      );
      return { data };
    }
    await delay(250);
    return {
      data: {
        sequence_id: sequenceId,
        step_count: 0,
        steps: [] as unknown[],
      },
    };
  },

  /** §10 — deliver a real test email (SMS gated by allowlisting). */
  async testSend(
    sequenceId: number,
    body: { recipient_email?: string; recipient_phone?: string },
    locationId?: number,
  ) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.post<Record<string, unknown>>(
        `${RECALL_V1}/sequences/${sequenceId}/test-send/`,
        body,
        { location_id: locationId },
      );
      return { data };
    }
    await delay(400);
    return {
      data: {
        sent: true,
        sequence_id: sequenceId,
        dispatches: [] as unknown[],
        errors: [] as string[],
        preview: {},
      },
    };
  },

  /** §11 — audit trail of edits (versions). */
  async getVersionHistory(sequenceId: number, locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.get<{ history: unknown[] }>(
        `${RECALL_V1}/sequences/${sequenceId}/version-history/`,
        { location_id: locationId },
      );
      return { data };
    }
    await delay(200);
    return { data: { history: [] as unknown[] } };
  },

  /** §12 — starter library templates. */
  async listSequenceTemplates(
    params: { tag?: string; search?: string; template_type?: string } = {},
    locationId?: number,
  ) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.get<unknown[]>(`${RECALL_V1}/sequence-templates/`, {
        ...params,
        location_id: locationId,
      });
      return { data };
    }
    await delay(200);
    return { data: [] as unknown[] };
  },

  /** §13 — block palette metadata for the canvas. */
  async getBlocksPalette(locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.get<{ blocks: unknown[]; recommended?: Record<string, string[]> }>(
        `${RECALL_V1}/sequence-builder/blocks/`,
        { location_id: locationId },
      );
      return { data };
    }
    await delay(200);
    return { data: { blocks: [] as unknown[], recommended: {} } };
  },

  /** Related — message templates referenced by slug in action nodes. */
  async listMessageTemplates(
    params: { channel?: string; search?: string } = {},
    locationId?: number,
  ) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.get<unknown[] | { results?: unknown[] }>(
        `${RECALL_BASE}/message-templates/`,
        { ...params, location_id: locationId },
      );
      const list = Array.isArray(data) ? data : (data?.results ?? []);
      return { data: list };
    }
    await delay(200);
    const channel = params.channel;
    if (channel === 'voice' || channel === 'call') {
      return { data: recallCallTemplates };
    }
    return { data: recallEmailTemplates };
  },

  /** Related — smart tag registry for template tokens. */
  async listSmartTags(locationId?: number) {
    const rt = getRecallRuntime();
    if (rt) {
      const data = await rt.get<unknown>(`${RECALL_V1}/smart-tags/`, {
        location_id: locationId,
      });
      const list = parseSmartTagsResponse(data);
      return { data: list };
    }
    await delay(150);
    return { data: recallSmartTags };
  },
};

export const MOCK_RECALL_LOCATION_ID = MOCK_LOCATION_ID;
